/** React libraries */
import React, { useEffect, useState } from 'react';
import { Col, Row } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { BsXCircle } from 'react-icons/bs';
import { useForm, FormProvider} from "react-hook-form";
import { IconBaseProps } from 'react-icons';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

/** Custom components types, services */
import RequestDetail from '../CommonComponents/RequestDetail';
import DocumentUpload from '../CommonComponents/Document-Upload';
import LogoRequestDiscussion from '../CommonComponents/LogoRequestDiscussion';
import CustomAccordion from '../../../components/Accordion/Accordion';
import Loader from '../../../components/Loader/Loader';
import ConfirmationModal from '../../../components/Confirmation-Modal/Confirmation-Modal';
import oidcConfig from '../../../utils/oidcConfig';
import DescOfProduct from '../CommonComponents/Description-of-Product';

import { LogoRequestDetails, LogoRequestConstants, ProductTypeString } from '../logoRequest.Type';
import { getStatusLabel, getStatusStyles, STATUS } from '../../../utils/statusHandler';
import { dateConversion, formatDate, toUTCFormatDate } from '../../../utils/dateHandler';
import { logoRequestService } from '../logoRequestService';
import { AppDispatch } from '../../../store/store';
import { updateLogoRequest } from "../../LogoRequestList/logoRequestSlice";


/** Styles */
import "./ProductLogoRequestDetails.style.css";

interface logoRequestDetailsProps {
    initialData: LogoRequestDetails;
}

const ProductLogoRequestDetails: React.FC<logoRequestDetailsProps> = (requestData) => {

    const methods = useForm({
        defaultValues: requestData.initialData,
    });
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [showUnsaveChangesConfirmationPrompt, setUnsaveChangesConfirmationPrompt] = useState(false);
    const [showSubmitLogoRequestConfirmation, setSubmitLogoRequestConfirmation] = useState(false);
    const [showDeleteLogoRequestConfirmation, setDeleteLogoRequestConfirmation] = useState(false);
    const status = (requestData.initialData.status) ? requestData.initialData.status : STATUS.DRAFT;
    const isReadOnly = (requestData.initialData.isReadOnly) ? requestData.initialData.isReadOnly : false;
    const statusStyle = status ? getStatusStyles(status) : { backgroundColor: 'gray' };
    const BsXCircleCustom = BsXCircle as React.ComponentType<IconBaseProps>;
    const [loadingUpdatLogoRequest, setLoadingUpdatLogoRequest] = useState(false);

    // Prompt on browser refresh/close
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (methods.formState.isDirty) {
                e.preventDefault();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [methods.formState.isDirty]);


    useEffect(() => {
        // Reset the form when context changes
        methods.reset({
            ...methods.getValues(),
            documentShareFilePath: requestData.initialData.documentShareFilePath,
            agreementShareFilePath: requestData.initialData.agreementShareFilePath
        });
    }, [requestData.initialData.documentShareFilePath, requestData.initialData.agreementShareFilePath]);

    /**
     * Navigate to previous page
     */
    const navigatToBack = () => {
        navigate(-1);
    }

    /**
     * On close button click
     */
    const onCloseButtonClick = () => {
        try {
            if (methods.formState.isDirty) {
                setUnsaveChangesConfirmationPrompt(true);
            } else {
                navigatToBack();
            }
        } catch (exe) {
            console.log(exe);
        }
    }

    /**
     * Submit logo request
     * @param data 
     * @param isForCancelled 
     * @returns 
     */
    const submitLogoRequest = async (data: any, isForCancelled: boolean = false) => {
        try {
            let submittedDate = data.submittedDate;
            let statusToUpdate = "";
            if (isForCancelled) {
                statusToUpdate = STATUS.CANCELLED;
                // If user selected for labelling aggrement and did not uploaded aggrement then set licencecode to No
                if (data.requestDetailsData.fscLicenceCode === 'Yes' && (!data.requestDetailsData.labelingAgreementPath || data.requestDetailsData.labelingAgreementPath == "")) {
                    data.requestDetailsData.fscLicenceCode === 'No';
                }
            } else {
                // Validate site selection: if sites are available, at least one must be selected
                const allSites = data.requestDetailsData.allSiteMetaData || [];
                const hasRealSites = allSites.length > 0 && allSites[0]?.id !== "N/A";
                if (hasRealSites && (!data.requestDetailsData.siteName || data.requestDetailsData.siteName.length === 0)) {
                    toast.error(LogoRequestConstants.SITE_SELECTION_REQUIRED);
                    return;
                }

                let currentDate = new Date();
                submittedDate = toUTCFormatDate(currentDate);
                statusToUpdate = STATUS.SUBMITTED;
                if (data.requestDetailsData.fscLicenceCode === 'Yes' && (!data.requestDetailsData.labelingAgreementPath || data.requestDetailsData.labelingAgreementPath == "")) {
                    toast.error(LogoRequestConstants.LABELLING_AGREEMENT_MISSING);
                    return;
                }
            }
            setLoadingUpdatLogoRequest(true);
            let result = await logoRequestService.updateLogoRequest(data, statusToUpdate, submittedDate);
            if (result.isSuccess) {
                let siteNames = "";
                if (data.requestDetailsData.siteName?.length > 0) {
                    siteNames = data.requestDetailsData.siteName.join(", ");
                }
                dispatch(updateLogoRequest({
                    requestId: requestData.initialData.requestId,
                    requestName: requestData.initialData.requestName,
                    requestType: requestData.initialData.requestType,
                    submittedDate: result.submittedDate,
                    status: statusToUpdate,
                    jobName: data.requestDetailsData.jobName,
                    jobId: data.requestDetailsData.jobNumber,
                    siteName: siteNames,
                    convertedSubmittedDate: dateConversion(result.submittedDate, "MMMM d, yyyy"),
                    displayStatus: getStatusLabel(statusToUpdate)
                }));

                methods.reset(data);
                setLoadingUpdatLogoRequest(false);
                navigatToBack();
                let suceessMessage = result?.message || LogoRequestConstants.LOGO_REQUEST_SUCCESSFULLY_SUBMITTED_MSG;
                if (isForCancelled) {
                    suceessMessage = LogoRequestConstants.LOGO_REQUEST_CANCEL_SUCCESSFULL_MSG;
                }
                toast.success(suceessMessage);
            } else {
                setLoadingUpdatLogoRequest(false);
                let errorMessage = result?.message || LogoRequestConstants.LOGO_REQUEST_SUBMITTED_FAIL_MSG;
                if (isForCancelled) {
                    errorMessage = LogoRequestConstants.LOGO_REQUEST_CANCEL_FAIL_MSG;
                }
                toast.error(errorMessage);
            }
        } catch (error: any) {
            setLoadingUpdatLogoRequest(false);
            const errorMessage = error?.response?.data?.message || LogoRequestConstants.LOGO_REQUEST_SUBMITTED_FAIL_MSG;
            toast.error(errorMessage);
        }
    }

    /**
     * On submut button click
     * @param data 
     */
    const onSubmit = () => {
        setSubmitLogoRequestConfirmation(true);
    };

    /**
     * On submut button click
     * @param data 
     */
    const onFSCMediaButtonCick = () => {
        window.open(oidcConfig.fscMediaKitLink, "_blank", "noopener,noreferrer");
    };

    /**
     * On confirmation prompt save changes
     */
    const onSaveChanges = () => {
        try {
            setUnsaveChangesConfirmationPrompt(false);
            const data = methods.getValues();
            submitLogoRequest(data);
        } catch (exe) {
            console.log(exe);
        }
    }

    /**
     * On confirmation prompt discard changes
     */
    const onDiscardChanges = () => {
        try {
            setUnsaveChangesConfirmationPrompt(false);
            const data = methods.getValues();
            methods.reset(data);
            navigatToBack();
        } catch (exe) {
            console.log(exe);
        }
    }

    /**
     * On confirmation prompt hide
     */
    const onConfirmModalHide = () => {
        setUnsaveChangesConfirmationPrompt(false);
    }

    /**
     * Return request status info
     * @returns 
     */
    const reuestHeaderStatus = () => {
        try {
            let statusValue = requestData.initialData.displayStatus
            if (requestData.initialData.status === STATUS.DRAFT) {
                statusValue = 'New request : ' + requestData.initialData.requestType;
            }
            return statusValue;
        } catch (exe) {
            console.log(exe);
        }
    }

    /**
     * Delete logo request
     */
    const deleteLogoRequest = () => {
        setDeleteLogoRequestConfirmation(true);
    }

    /**
     * On submit logo request confiramtion
     */
    const onConfirmSubmitLogoRequest = () => {
        setSubmitLogoRequestConfirmation(false);
        const data = methods.getValues();
        submitLogoRequest(data);
    }

    /**
     * On submit logo request confirmation cancel
     */
    const onCancelSubmitLogoRequest = () => {
        setSubmitLogoRequestConfirmation(false);
    }

    /**
     * On delete logo request confiramtion
     */
    const onConfirmDeleteLogoRequest = () => {
        setDeleteLogoRequestConfirmation(false);
        const data = methods.getValues();
        submitLogoRequest(data, true);
    }

    /**
     * On delete logo request confirmation cancel
     */
    const onCancelDeleteLogoRequest = () => {
        setDeleteLogoRequestConfirmation(false);
    }

    /**
     * On submit labelling agreement
     */
    const onSubmitLabelAgreement = async () => {
        try {
            setLoadingUpdatLogoRequest(true);
            const data = methods.getValues();
            let statusToUpdate = data.status;
            //data.initialData.requestDetailsData.fscLicenceCode = "Yes";
            let result = await logoRequestService.updateLogoRequest(data, statusToUpdate, data.submittedDate);
            if (result.isSuccess) {
                let siteNames = "";
                if (data.requestDetailsData.siteName?.length > 0) {
                    siteNames = data.requestDetailsData.siteName.join(", ");
                }
                dispatch(updateLogoRequest({
                    requestId: requestData.initialData.requestId,
                    requestName: requestData.initialData.requestName,
                    requestType: requestData.initialData.requestType,
                    submittedDate: result.submittedDate,
                    status: statusToUpdate,
                    jobName: data.requestDetailsData.jobName,
                    jobId: data.requestDetailsData.jobNumber,
                    siteName: siteNames,
                    convertedSubmittedDate: dateConversion(result?.submittedDate, "MMMM d, yyyy"),
                    displayStatus: getStatusLabel(statusToUpdate)
                }));
                setLoadingUpdatLogoRequest(false);
                let suceessMessage = result?.message || LogoRequestConstants.LOGO_REQUEST_SUCCESSFULLY_SUBMITTED_MSG;
                toast.success(suceessMessage);
            } else {
                setLoadingUpdatLogoRequest(false);
                let errorMessage = result?.message || LogoRequestConstants.LOGO_REQUEST_SUBMITTED_FAIL_MSG;
                toast.error(errorMessage);
            }
        } catch (error: any) {
            setLoadingUpdatLogoRequest(false);
            const errorMessage = error?.response?.data?.message || LogoRequestConstants.LOGO_REQUEST_SUBMITTED_FAIL_MSG;
            toast.error(errorMessage);
        }
    }

    return (
        <>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <div className="d-flex justify-content-between align-items-center pt-2">
                        {loadingUpdatLogoRequest && <div className="loader-overlay"><Loader /></div>}
                        <Row className="w-100">
                            <Col md={9} className="request-status-container">
                                <div className="d-flex flex-wrap align-items-center gap-3" style={{ gap: '0.5rem', flexGrow: 1 }}>
                                    <div className='request-name'>
                                        {requestData.initialData.requestName}
                                    </div>
                                    <div
                                        className="d-flex align-items-center text-capitalize request-header-status rounded p-1 px-2"
                                        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                    >
                                        <div
                                            className="rounded-circle mr-2 status-badge"
                                            style={{
                                                backgroundColor: statusStyle.backgroundColor,
                                                width: "10px",
                                                height: "10px",
                                                textAlign: "start",
                                            }}
                                        />
                                        {reuestHeaderStatus()}
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} className='d-flex justify-content-end align-items-center gap-4'>
                                {status == 'Approved' && <button type="button" className="d-flex align-items-center btn btn-primary btn-fsc-media" onClick={onFSCMediaButtonCick}>Go to FSC Media Kit</button>}
                                <button type="button" onClick={onCloseButtonClick} className='close-button' aria-label="Close">
                                    <BsXCircleCustom size={30} color="black" />
                                </button>
                            </Col>
                            {status == 'Change Requested' && <Col md={12}>
                                <div className="d-flex flex-wrap align-items-center">
                                    Expected resolution date : **/**/****
                                </div>
                            </Col>}
                        </Row>
                    </div>
                    <hr className="mx-4  mb-1"></hr>
                    <Row className="pt-3 px-4">
                        <Col>
                            <CustomAccordion
                                key={1}
                                title="Description of Product"
                                data={
                                    <RequestDetail onSubmitLabelAgreement={onSubmitLabelAgreement} />
                                }
                                isOpen={true}
                                toggleAccordion={() => { }}
                            />
                        </Col>
                    </Row>
                    <Row className="px-4">
                        <Col xl={6} lg={6} md={6}>
                            <CustomAccordion
                                key={2}
                                title="Description of Product"
                                data={
                                    <DescOfProduct />
                                }
                                isOpen={true}
                                toggleAccordion={() => { }}
                            />

                            <CustomAccordion
                                key={3}
                                title="Documentations upload"
                                data={
                                    <DocumentUpload />
                                }
                                isOpen={true}
                                toggleAccordion={() => { }}
                            />
                        </Col>

                        <Col xl={6} lg={6} md={6}>
                            <CustomAccordion
                                key={4}
                                title="Logo Request Discussion"
                                data={
                                    <LogoRequestDiscussion />
                                }
                                isOpen={true}
                                toggleAccordion={() => { }}
                            />
                        </Col>
                    </Row>
                    {
                        (!isReadOnly && status == STATUS.DRAFT) && <>
                            <Row className="px-4">
                                <div>
                                    This trademark approval is based upon the information provided in this form by you, the certificate holder. Trademark approval is not an assurance that the product or the paper used is eligible for labeling or promotion. Your company must review incoming documentation from your supplier(s) for their FSC claim and certificate code. The approval is for the use stated and is invalid if applied to a different product, document, or item.
                                </div>
                            </Row>
                        </>
                    }
                    {!isReadOnly && 
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            {status == STATUS.DRAFT && <button type="button" className="d-flex align-items-center btn btn-danger rounded-pill" onClick={deleteLogoRequest}>Cancel Request</button>}
                            <button disabled={requestData.initialData.requestType == ProductTypeString.OnProduct && !requestData.initialData.agreementShareFilePath}type="submit" className="d-flex align-items-center btn btn-primary rounded-pill">Submit Request</button>
                        </div>
                    }
                </form>
            </FormProvider>
            {/**  Unsave changes confirmation prompt */}
            <ConfirmationModal
                show={showUnsaveChangesConfirmationPrompt}
                title="Confirm"
                message={LogoRequestConstants.UNSAVED_CHANGES_CONFRIMATION}
                onConfirm={onSaveChanges}
                onCancel={onDiscardChanges}
                onHide={onConfirmModalHide}
                cancelBtnText="Discard Changes"
                confirmBtnText="Finish this request"
            />

            {/**  Submit request confirmation */}
            <ConfirmationModal
                show={showSubmitLogoRequestConfirmation}
                title="Confirm"
                message={LogoRequestConstants.LOGO_REQUEST_SUBMIT_CONFIRMATION}
                onConfirm={onConfirmSubmitLogoRequest}
                onCancel={onCancelSubmitLogoRequest}
                onHide={onCancelSubmitLogoRequest}
                cancelBtnText="No"
                confirmBtnText="Yes"
            />

            {/** Delete request confirmation */}
            <ConfirmationModal
                show={showDeleteLogoRequestConfirmation}
                title="Confirm"
                message={LogoRequestConstants.LOGO_REQUEST_CANCEL_CONFIRMATION}
                onConfirm={onConfirmDeleteLogoRequest}
                onCancel={onCancelDeleteLogoRequest}
                onHide={onCancelDeleteLogoRequest}
                cancelBtnText="No"
                confirmBtnText="Yes"
            />
        </>
    );
};

export default ProductLogoRequestDetails;
